import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template129: Menú/Plan de viaje de Seúl
// Mismo diseño y layout que Page2.jsx de guia-seul; datos desde page.data
const Template129 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const pageData = page?.data || {};
	const diasRaw = Array.isArray(pageData.dias) ? pageData.dias : [];
	const extrasRaw = Array.isArray(pageData.extras) ? pageData.extras : [];

	// Si la guía define sus propios mapeos (ej. Kuala Lumpur), usarlos; si no, valores por defecto (Seúl)
	const diasTemplateMapping =
		Array.isArray(pageData.diasTemplateMapping) &&
		pageData.diasTemplateMapping.length > 0
			? pageData.diasTemplateMapping
			: [130, 132, 134, 136, 138];
	const extrasTemplateMapping =
		Array.isArray(pageData.extrasTemplateMapping) &&
		pageData.extrasTemplateMapping.length > 0
			? pageData.extrasTemplateMapping
			: [140, 146, 141, 142, 143];

	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};
	// Para navegación: usar id si existe, si no el índice en pages (guías prebuilt suelen tener id = índice)
	const getPageIdOrIndex = (targetPage) => {
		if (!targetPage) return null;
		const id = targetPage?.id ?? targetPage?.data?.id;
		if (id != null) return id;
		const idx = pages.indexOf(targetPage);
		return idx >= 0 ? idx : null;
	};

	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		return {
			...dia,
			pageId: dia.pageId ?? getPageIdOrIndex(targetPage),
		};
	});

	const extras = extrasRaw
		.map((extra, index) => {
			if (extra.titulo === "Kofi" || extra.image?.includes("kofi")) return null;
			const templateId = extrasTemplateMapping[index] ?? null;
			const targetPage = templateId ? findPageByTemplateId(templateId) : null;
			return {
				...extra,
				pageId: extra.pageId ?? getPageIdOrIndex(targetPage),
			};
		})
		.filter(Boolean);

	const actualHeaderColor = headerBackgroundColor || "#2E8B80";
	const headerText = page?.texts?.[0]?.content ?? "Plan de viaje";

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const logoImage = getCanvasImageUrl("logo");

	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) onNavigateToPage(pageId);
	};

	const noList = page?.noList ?? page?.nolist ?? false;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA - igual que Page2 */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* CONTENIDO - mismo padding y estructura que Page2 */}
			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				{/* DÍAS - columna (uno debajo de otro) en móvil y desktop */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: isSmallScreen ? "10px" : "20px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							onClick={() => dia.pageId && handleNavigate(dia.pageId)}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								width: isSmallScreen ? "100%" : "auto",
								cursor: dia.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: isSmallScreen ? 0 : "20px",
								"&:hover": dia.pageId
									? {
											transform: "translateY(-5px)",
											"& .dia-box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{dia.image && (
								<Box
									component="img"
									src={dia.image}
									alt=""
									sx={{
										width: isSmallScreen ? 35 : 45,
										zIndex: 100,
										position: "absolute",
										left: isSmallScreen ? 8 : -20,
									}}
								/>
							)}
							<Box
								className="dia-box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: isSmallScreen ? "18px 16px" : "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: isSmallScreen ? 0 : "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: isSmallScreen ? "100%" : "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: isSmallScreen ? "38px" : "16px",
								}}
							>
								<Box
									sx={{
										fontSize: isSmallScreen ? "13px" : "13px",
										fontWeight: 600,
										color: "#333",
										marginLeft: isSmallScreen ? "10px" : "20px",
										whiteSpace: "pre-line",
									}}
								>
									Día {index + 1}:{" "}
									{isSmallScreen && dia.tituloMovil
										? dia.tituloMovil
										: dia.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				{/* SEPARADOR */}
				<Box
					component="h3"
					sx={{
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: "16px",
						marginBottom: "8px",
						color: "#333",
					}}
				>
					Información útil
				</Box>

				{/* EXTRAS - lista vertical o una línea de cuadrados (solo escritorio si noList) */}
				{noList && !isSmallScreen ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							flexWrap: "nowrap",
							gap: "12px",
							alignItems: "stretch",
							justifyContent: "stretch",
							width: "100%",
							pb: 0.5,
						}}
					>
						{extras.map((extra, index) => (
							<Box
								key={index}
								onClick={() => extra.pageId && handleNavigate(extra.pageId)}
								className="extra-box"
								sx={{
									flex: "1 1 0",
									minWidth: 0,
									minHeight: 100,
									backgroundColor: "#fff",
									borderRadius: "12px",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "10px 8px",
									cursor: extra.pageId ? "pointer" : "default",
									transition: "transform 0.2s ease, box-shadow 0.2s ease",
									boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
									"&:hover": extra.pageId
										? {
												transform: "translateY(-3px)",
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
											}
										: {},
								}}
							>
								{extra.image && (
									<Box
										component="img"
										src={extra.image}
										alt=""
										sx={{
											width: 54,
											height: 54,
											objectFit: "contain",
										}}
									/>
								)}
								<Box
									sx={{
										width: "100%",
										minHeight: "2.5em",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<Box
										component="span"
										sx={{
											fontSize: "11px",
											fontWeight: 600,
											color: "#333",
											textAlign: "center",
											lineHeight: 1.25,
											whiteSpace: "normal",
											display: "-webkit-box",
											WebkitLineClamp: 2,
											overflow: "hidden",
											wordBreak: "break-word",
											width: isSmallScreen ? "100%" : "auto",
										}}
									>
										{extra.titulo}
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: isSmallScreen ? "10px" : "20px",
							flexWrap: "nowrap",
						}}
					>
						{extras.map((extra, index) => (
							<Box
								key={index}
								onClick={() => extra.pageId && handleNavigate(extra.pageId)}
								sx={{
									borderRadius: "12px",
									padding: 0,
									display: "flex",
									alignItems: "center",
									gap: isSmallScreen ? "5px" : "10px",
									position: "relative",
									minHeight: 30,
									width: isSmallScreen ? "100%" : "auto",
									cursor: extra.pageId ? "pointer" : "default",
									transition: "transform 0.3s ease",
									marginLeft: isSmallScreen ? 0 : "20px",
									"&:hover": extra.pageId
										? {
												transform: "translateY(-5px)",
												"& .extra-box": {
													boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
												},
											}
										: {},
								}}
							>
								{extra.image && (
									<Box
										component="img"
										src={extra.image}
										alt=""
										sx={{
											width: isSmallScreen ? 35 : 45,
											zIndex: 100,
											position: "absolute",
											left: isSmallScreen ? 8 : -20,
										}}
									/>
								)}
								<Box
									className="extra-box"
									sx={{
										backgroundColor: "#fff",
										borderRadius: "12px",
										padding: isSmallScreen ? "18px 16px" : "18px 16px",
										display: "flex",
										alignItems: "center",
										gap: isSmallScreen ? 0 : "10px",
										position: "relative",
										minHeight: 30,
										width: "100%",
										marginLeft: 0,
										textAlign: "left",
										height: isSmallScreen ? "100%" : "auto",
										flex: 1,
										transition: "box-shadow 0.3s ease",
										paddingLeft: isSmallScreen ? "38px" : "16px",
									}}
								>
									<Box
										sx={{
											fontSize: isSmallScreen ? "13px" : "13px",
											fontWeight: 600,
											color: "#333",
											marginLeft: isSmallScreen ? "10px" : "20px",
											whiteSpace: "pre-line",
										}}
									>
										{extra.titulo}
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template129.thumbnail = "/images/template-1.png";

export default Template129;
